import React, { useEffect, useState } from 'react';
import { deleteApp, initializeApp } from 'firebase/app';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { collection, deleteDoc, doc, onSnapshot, orderBy, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { db, firebaseConfig } from '../firebase';
import { AccessDenied, SECTIONS, canAccess, normalizeAdmin } from '../adminAccess';

const DEFAULT_ACCESS = {
  dashboard: true,
  orders: true,
  communications: false,
  support: true,
  workers: false,
  cemeteries: false,
  packages: false,
  users: false,
  finance: false,
  adminAccess: false,
};

export default function AdminAccessPage({ user }) {
  const current = normalizeAdmin(user);
  const [admins, setAdmins] = useState([]);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    cityScope: 'all',
    hideMoney: true,
    access: DEFAULT_ACCESS,
  });

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'admin_access_requests'), where('status', '==', 'pending')),
      snap => setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err => console.error(err),
    );
    return () => unsub();
  }, []);

  async function approveRequest(req) {
    await setDoc(doc(db, 'admin_users', req.id), {
      name: req.name || req.id,
      email: req.id,
      authUid: '',
      role: 'manager',
      status: 'active',
      cityScope: 'all',
      hideMoney: true,
      access: { ...DEFAULT_ACCESS, finance: false, adminAccess: false },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    await deleteDoc(doc(db, 'admin_access_requests', req.id));
  }

  async function rejectRequest(req) {
    await deleteDoc(doc(db, 'admin_access_requests', req.id));
  }

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'admin_users'), orderBy('createdAt', 'desc')),
      snap => setAdmins(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return () => unsub();
  }, []);

  if (!canAccess(current, 'adminAccess')) return <AccessDenied title="Only the super admin can manage admin access" />;

  function updateAccess(key, value) {
    setForm(prev => ({ ...prev, access: { ...prev.access, [key]: value } }));
  }

  async function addAdmin(e) {
    e.preventDefault();
    const email = form.email.trim().toLowerCase();
    if (!email || !form.password.trim()) return;
    let authUid = '';
    try {
      const secondary = initializeApp(firebaseConfig, `admin-create-${Date.now()}`);
      const secondaryAuth = getAuth(secondary);
      const cred = await createUserWithEmailAndPassword(secondaryAuth, email, form.password);
      authUid = cred.user.uid;
      await deleteApp(secondary);
    } catch (err) {
      console.error(err);
    }
    await setDoc(doc(db, 'admin_users', email), {
      name: form.name.trim() || email,
      email,
      authUid,
      role: 'manager',
      status: 'active',
      cityScope: form.cityScope || 'all',
      hideMoney: true,
      access: { ...form.access, finance: false, adminAccess: false },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    setForm({ name: '', email: '', password: '', cityScope: 'all', hideMoney: true, access: DEFAULT_ACCESS });
  }

  async function toggleAdmin(admin) {
    await updateDoc(doc(db, 'admin_users', admin.id), {
      status: admin.status === 'disabled' ? 'active' : 'disabled',
      updatedAt: serverTimestamp(),
    });
  }

  async function saveAdmin(admin) {
    await updateDoc(doc(db, 'admin_users', admin.id), {
      access: admin.access || {},
      cityScope: admin.cityScope || 'all',
      hideMoney: true,
      updatedAt: serverTimestamp(),
    });
  }

  return (
    <div className="page-shell">
      <div className="mb-6">
        <h1 className="page-title">Admin users & section access</h1>
        <p className="text-sm text-gray-500 mt-1">Create managers, restrict city scope, and choose exactly which sections they can access. Money remains super-admin only.</p>
      </div>

      {requests.length > 0 && (
        <div className="mb-6 bg-white border border-amber-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
            <span>⏳</span>
            <h2 className="font-semibold text-gray-900 text-sm">
              Pending access requests ({requests.length})
            </h2>
          </div>
          <ul className="divide-y divide-gray-100">
            {requests.map(r => (
              <li key={r.id} className="px-5 py-3 flex items-center gap-3">
                {r.photoURL
                  ? <img src={r.photoURL} alt="" className="w-8 h-8 rounded-full" />
                  : <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                      {(r.name || r.id).charAt(0).toUpperCase()}
                    </div>}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{r.name || r.id}</p>
                  <p className="text-xs text-gray-500 truncate">{r.id} · via {r.provider || 'unknown'}</p>
                </div>
                <button
                  onClick={() => approveRequest(r)}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary text-white hover:opacity-90"
                >
                  Approve
                </button>
                <button
                  onClick={() => rejectRequest(r)}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  Reject
                </button>
              </li>
            ))}
          </ul>
          <p className="px-5 py-2.5 text-xs text-gray-500 bg-gray-50 border-t border-gray-100">
            Approved accounts start as <strong>managers</strong> with no finance or admin-access rights. Adjust their sections below.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <form onSubmit={addAdmin} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 h-fit">
          <h2 className="font-bold text-gray-900 mb-4">Add admin user</h2>
          <div className="space-y-3">
            <input className="input-field w-full" placeholder="Full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            <input className="input-field w-full" type="email" placeholder="Email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            <input className="input-field w-full" type="password" placeholder="Temporary password" required value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
            <select className="input-field w-full" value={form.cityScope} onChange={e => setForm({ ...form, cityScope: e.target.value })}>
              <option value="all">All cities</option>
              <option value="baku">Baku only</option>
              <option value="ganja">Ganja only</option>
              <option value="sumqayit">Sumqayit only</option>
            </select>
          </div>
          <div className="mt-5">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Section access</p>
            <div className="grid grid-cols-2 gap-2">
              {SECTIONS.filter(s => !['finance', 'adminAccess'].includes(s.key)).map(s => (
                <label key={s.key} className="flex items-center gap-2 text-sm rounded-xl border border-gray-100 px-3 py-2">
                  <input type="checkbox" checked={!!form.access[s.key]} onChange={e => updateAccess(s.key, e.target.checked)} />
                  {s.label}
                </label>
              ))}
            </div>
          </div>
          <button className="btn-primary w-full mt-5">Create admin user</button>
        </form>

        <div className="xl:col-span-2 space-y-4">
          {admins.map(admin => (
            <AdminCard key={admin.id} admin={admin} onSave={saveAdmin} onToggle={toggleAdmin} />
          ))}
          {admins.length === 0 && <div className="bg-white rounded-2xl p-12 text-center text-gray-400">No admin users created yet.</div>}
        </div>
      </div>
    </div>
  );
}

function AdminCard({ admin, onSave, onToggle }) {
  const [draft, setDraft] = useState(admin);

  useEffect(() => setDraft(admin), [admin]);

  function accessChange(key, value) {
    setDraft(prev => ({ ...prev, access: { ...(prev.access || {}), [key]: value } }));
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <p className="font-bold text-gray-900">{draft.name || draft.email}</p>
          <p className="text-sm text-gray-500">{draft.email}</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => onToggle(admin)}>{draft.status === 'disabled' ? 'Enable' : 'Disable'}</button>
          <button className="btn-primary" onClick={() => onSave(draft)}>Save access</button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <select className="input-field" value={draft.cityScope || 'all'} onChange={e => setDraft({ ...draft, cityScope: e.target.value })}>
          <option value="all">All cities</option>
          <option value="baku">Baku only</option>
          <option value="ganja">Ganja only</option>
          <option value="sumqayit">Sumqayit only</option>
        </select>
        <div className="rounded-xl bg-red-50 text-red-700 px-3 py-2 text-sm font-semibold">Money hidden</div>
        <div className={`rounded-xl px-3 py-2 text-sm font-semibold ${draft.status === 'disabled' ? 'bg-gray-100 text-gray-600' : 'bg-green-50 text-green-700'}`}>{draft.status || 'active'}</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
        {SECTIONS.filter(s => !['finance', 'adminAccess'].includes(s.key)).map(s => (
          <label key={s.key} className="flex items-center gap-2 text-sm rounded-xl border border-gray-100 px-3 py-2">
            <input type="checkbox" checked={!!draft.access?.[s.key]} onChange={e => accessChange(s.key, e.target.checked)} />
            {s.label}
          </label>
        ))}
      </div>
    </div>
  );
}
